// frontend/src/services/agent/AgentHTTPClient.ts
/**
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 🚀 NeuroAgentes Platform - Intelligent Agent Development
 * 
 * AUDITORIA: Cliente HTTP para operações de agentes.
 * Centraliza todas as chamadas de API relacionadas a agentes.
 */

import api from "../api";
import { Agent } from "../../types";
import { createLogger, executeWithErrorHandling } from "../../utils/logger";
import { extractErrorMessage } from "../../utils/errorHandling";

const logger = createLogger('AgentHTTPClient');

// Interfaces para respostas da API
export interface AgentListResponse {
  success: boolean;
  count: number;
  data: Agent[];
}

export interface AgentResponse {
  success: boolean;
  data: Agent;
  message?: string;
}

export interface QRCodeResponse {
  success: boolean;
  data: {
    qrcode: string;
  };
}

export interface AgentStatusResponse {
  success: boolean;
  data: {
    isConnected: boolean;
    status: string;
  };
}

/**
 * Cliente HTTP para operações de agentes
 */
export class AgentHTTPClient {
  /**
   * Busca lista de agentes por projeto
   */
  async getAgentsByProject(projectId: string): Promise<Agent[]> {
    return executeWithErrorHandling(
      async () => {
        if (!projectId) {
          throw new Error('ID do projeto é obrigatório');
        }

        logger.debug('Buscando agentes', { projectId });

        // Tentar novo endpoint primeiro
        try {
          const response = await api.get<AgentListResponse>(
            `/api/admin/agents/projects/${projectId}/list`
          );
          
          if (response.data.success && Array.isArray(response.data.data)) {
            logger.info(`Encontrados ${response.data.data.length} agentes`);
            return response.data.data;
          }
        } catch (error) {
          logger.debug('Endpoint novo falhou, tentando alternativo');
        }

        // Fallback para endpoint antigo
        const response = await api.get<AgentListResponse>(
          `/api/admin/agents/organizations/${projectId}/list`
        );

        if (response.data.success && Array.isArray(response.data.data)) {
          logger.info(`Encontrados ${response.data.data.length} agentes (endpoint alternativo)`);
          return response.data.data;
        }

        return [];
      },
      logger,
      'buscar agentes por projeto'
    );
  }

  /**
   * Busca agente por ID
   */
  async getAgentById(agentId: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Buscando agente por ID', { agentId });
        
        const response = await api.get<AgentResponse>(`/api/admin/agents/${agentId}`);
        
        if (response.data.success && response.data.data) {
          logger.info('Agente encontrado');
          return response.data.data;
        }
        
        throw new Error('Agente não encontrado');
      },
      logger,
      'buscar agente por ID'
    );
  }

  /**
   * Cria novo agente
   */
  async createAgent(agentData: Partial<Agent>): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Criando novo agente');
        
        const response = await api.post<AgentResponse>('/api/admin/agents', agentData);
        
        if (response.data.success && response.data.data) {
          logger.info('Agente criado com sucesso', { agentId: response.data.data.id });
          return response.data.data;
        }
        
        throw new Error(response.data.message || 'Erro ao criar agente');
      },
      logger,
      'criar agente'
    );
  }

  /**
   * Atualiza agente existente
   */
  async updateAgent(agentId: string, agentData: Partial<Agent>): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Atualizando agente', { agentId });
        
        const response = await api.put<AgentResponse>(`/api/admin/agents/${agentId}`, agentData);
        
        if (response.data.success && response.data.data) {
          logger.info('Agente atualizado com sucesso');
          return response.data.data;
        }
        
        throw new Error(response.data.message || 'Erro ao atualizar agente');
      },
      logger,
      'atualizar agente'
    );
  }

  /**
   * Remove agente
   */
  async deleteAgent(agentId: string): Promise<void> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Removendo agente', { agentId });
        
        await api.delete(`/api/admin/agents/${agentId}`);
        
        logger.info('Agente removido com sucesso');
      },
      logger,
      'remover agente'
    );
  }

  /**
   * Obtém QR Code para conexão do agente
   */
  async getQRCode(agentId: string): Promise<string> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Obtendo QR Code', { agentId });
        
        const response = await api.get<QRCodeResponse>(`/api/admin/agents/${agentId}/qr`);
        
        if (response.data.success && response.data.data?.qrcode) {
          logger.info('QR Code obtido com sucesso');
          return response.data.data.qrcode;
        }
        
        throw new Error('QR Code não disponível');
      },
      logger,
      'obter QR Code'
    );
  }

  /**
   * Verifica status de conexão do agente
   */
  async getConnectionStatus(agentId: string): Promise<{ isConnected: boolean; status: string }> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Verificando status de conexão', { agentId });
        
        const response = await api.get<AgentStatusResponse>(`/api/admin/agents/${agentId}/status`);
        
        if (response.data.success && response.data.data) {
          logger.debug('Status obtido', { status: response.data.data.status });
          return response.data.data;
        }
        
        return { isConnected: false, status: 'unknown' };
      },
      logger,
      'verificar status de conexão'
    );
  }

  /**
   * Ativa agente (usando rota específica)
   */
  async activateAgent(agentId: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Ativando agente', { agentId });
        
        const response = await api.post<AgentResponse>(`/api/admin/agents/${agentId}/activate`);
        
        if (response.data.success && response.data.data) {
          logger.info('Agente ativado com sucesso');
          return response.data.data;
        }
        
        throw new Error(response.data.message || 'Erro ao ativar agente');
      },
      logger,
      'ativar agente'
    );
  }

  /**
   * Desativa agente (usando rota específica)
   */
  async deactivateAgent(agentId: string): Promise<Agent> {
    return executeWithErrorHandling(
      async () => {
        logger.debug('Desativando agente', { agentId });
        
        const response = await api.post<AgentResponse>(`/api/admin/agents/${agentId}/deactivate`);
        
        if (response.data.success && response.data.data) {
          logger.info('Agente desativado com sucesso');
          return response.data.data;
        }
        
        throw new Error(response.data.message || 'Erro ao desativar agente');
      },
      logger,
      'desativar agente'
    );
  }
}